import { fetchEventSource } from '@microsoft/fetch-event-source';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { sourcesContract, type ChatSource } from '@archie/api-shared';
import logo from '../../../assets/images/archieIcon.svg';
import logoDark from '../../../assets/images/archieIconDark.svg';
import { useConfig } from '../../../contexts/ConfigContext';
import { QueryApiContext } from '../../../contexts/QueryContext';
import LoadingSpinner from '../../../components/loadingSpinner/LoadingSpinner';
import { z } from 'zod';
import ChatCountBubble from '../../../components/chatCountBubble/ChatCountBubble';
import ChatBubble, { type AssistantStep } from '../../../components/chatBubble/ChatBubble';
import EditableTitle from '../../../components/editableTitle/EditableTitle';
import { useActiveTenant } from '../../../contexts/ActiveTenantContext';
import { computeXAmzContentSha256 } from '../../../utils/signedFetch';
import { Button, AlertBanner, Input, Dialog, DialogTitle, DialogBody, DialogActions } from '../../../components/ui';
import {
  ChatBubbleLeftIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import AttachmentChip from '../../../components/composer/AttachmentChip';
import { useAttachments } from '../../../components/composer/useAttachments';
import { useQueryClient } from '@tanstack/react-query';
import { useShowReasoningPref } from '../../../hooks/useShowReasoningPref';
import { getToolStatusLabel, DRAFTING_STATUS, THINKING_STATUS } from './toolStatusLabels';
import { sourceKey } from '../../../components/conversationSourcesPanel/sourceKey';
import SourcesDialog from '../../../components/conversationSourcesPanel/SourcesDialog';
import SourcesBadge from '../../../components/conversationSourcesPanel/SourcesBadge';

type AssistantBlock =
  | { kind: 'text'; text: string }
  | { kind: 'reasoning'; text: string }
  | { kind: 'tool_call'; toolName: string; input: unknown };

export interface ChatAttachmentView {
  id: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  kind: 'image' | 'document';
  width?: number | null;
  height?: number | null;
}

type ChatMessage =
  | { role: 'user'; content: string; attachments?: ChatAttachmentView[] }
  | { role: 'assistant'; content: string; steps?: AssistantStep[]; sources?: ChatSource[] };

type LoadedMessage =
  | { role: 'user'; content: string; attachments?: ChatAttachmentView[] }
  | { role: 'assistant'; content: string; blocks?: AssistantBlock[]; sources?: ChatSource[] | null }
  | { role: 'tool'; content: string; name: string };

const dedupChatSources = (sources: ChatSource[]): ChatSource[] => {
  const seen = new Set<string>();
  const out: ChatSource[] = [];
  for (const s of sources) {
    const key = sourceKey(s);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
};

/**
 * Group raw history messages into chat-bubble messages. Tool messages are
 * folded into the immediately preceding assistant message as `tool_result`
 * steps; reasoning + tool_call blocks live alongside them in the same
 * collapsible Steps section. Source attributions thread to the assistant
 * message they were cited in.
 */
const groupHistory = (messages: LoadedMessage[]): ChatMessage[] => {
  const out: ChatMessage[] = [];
  for (const msg of messages) {
    if (msg.role === 'user') {
      out.push({ role: 'user', content: msg.content, attachments: msg.attachments });
    } else if (msg.role === 'assistant') {
      const steps: AssistantStep[] = [];
      for (const b of msg.blocks ?? []) {
        if (b.kind === 'reasoning') steps.push({ kind: 'reasoning', text: b.text });
        else if (b.kind === 'tool_call') steps.push({ kind: 'tool_call', toolName: b.toolName, input: b.input });
      }
      out.push({
        role: 'assistant',
        content: msg.content,
        steps: steps.length > 0 ? steps : undefined,
        sources: msg.sources && msg.sources.length > 0 ? msg.sources : undefined
      });
    } else if (msg.role === 'tool') {
      const last = out[out.length - 1];
      if (last && last.role === 'assistant') {
        const next: AssistantStep = { kind: 'tool_result', toolName: msg.name, output: msg.content };
        last.steps = [...(last.steps ?? []), next];
      }
    }
  }
  return out;
};

type Source = z.infer<(typeof sourcesContract.get.responses)['200']>;

const Chat: React.FC = () => {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [chatting, setChatting] = useState(false);
  const [chatTitle, setChatTitle] = useState<string>('');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [foundSources, setFoundSources] = useState<Source[]>([]);
  const [currentChatContent, setCurrentChatContent] = useState<string>('');
  const [currentSteps, setCurrentSteps] = useState<AssistantStep[]>([]);
  const [currentSources, setCurrentSources] = useState<ChatSource[]>([]);
  const [currentStatus, setCurrentStatus] = useState<string | null>(null);
  const [highlightedSourceKey, setHighlightedSourceKey] = useState<string | null>(null);
  const [isSourcesDialogOpen, setIsSourcesDialogOpen] = useState(false);
  const [showReasoning, setShowReasoning] = useShowReasoningPref();
  const [chatError, setChatError] = useState<Error | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const [currentKnowledgeBases, setCurrentKnowledgeBases] = useState<string[]>([]);
  const [selectedAssistantId, setSelectedAssistantId] = useState<string | undefined>(undefined);
  const [isKbModalOpen, setIsKbModalOpen] = useState(false);
  const [isAssistantModalOpen, setIsAssistantModalOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const { config } = useConfig();
  const { id: chatId } = useParams<{ id: string }>();
  const restQueryClient = useContext(QueryApiContext);
  const { activeTenantId } = useActiveTenant();

  const {
    attachments,
    addFiles,
    removeAttachment,
    retryUpload,
    clearAttachments,
    isUploading: isAttachmentUploading,
    readyAttachmentIds,
    globalError: attachmentGlobalError
  } = useAttachments({
    apiUrl: config.api.apiUrl,
    tenantId: activeTenantId ?? null,
    conversationId
  });

  // Sticky-scroll state. Tracked as a ref so streaming token-by-token updates
  // don't cause re-renders just to evaluate "should we scroll." A scroll event
  // listener flips it based on the user's actual scroll position; programmatic
  // scrollTo calls also fire scroll events but always land at the bottom, so
  // the ref stays consistent.
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const chatContentRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);

  // Fetch knowledge bases from API for the active tenant
  const { data, isLoading, isError } = restQueryClient.knowledgeBases.list.useQuery(
    ['knowledgeBases', activeTenantId],
    {
      query: { tenantId: activeTenantId || undefined }
    },
    {
      enabled: !!activeTenantId
    }
  );

  // Fetch assistants from API for the active tenant
  const {
    data: assistantsData,
    isLoading: isAssistantsLoading,
    isError: isAssistantsError
  } = restQueryClient.assistants.list.useQuery(
    ['assistants', activeTenantId],
    {
      query: { tenantId: activeTenantId || '' }
    },
    {
      enabled: !!activeTenantId
    }
  );

  // Chat conversation mutations
  const createConversationMutation = restQueryClient.chatConversations.create.useMutation();
  const queryClient = useQueryClient();
  const updateConversationMutation = restQueryClient.chatConversations.update.useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatConversations'] });
    }
  });

  const handleTitleSave = useCallback(
    async (newTitle: string) => {
      if (!conversationId) return;
      const previous = chatTitle;
      setChatTitle(newTitle);
      try {
        await updateConversationMutation.mutateAsync({
          params: { id: conversationId },
          body: { title: newTitle }
        });
      } catch (err) {
        setChatTitle(previous);
        setChatError(err instanceof Error ? err : new Error('Failed to update title'));
      }
    },
    [conversationId, chatTitle, updateConversationMutation]
  );

  // Aggregate every source cited across the conversation so the sources panel
  // and header badge can render a single deduped list. Combines persisted
  // history with any sources arriving on the in-flight stream.
  const aggregatedSources = useMemo(() => {
    const all: ChatSource[] = [];
    for (const m of chatHistory) {
      if (m.role === 'assistant' && m.sources) all.push(...m.sources);
    }
    all.push(...currentSources);
    return dedupChatSources(all);
  }, [chatHistory, currentSources]);

  // Selecting a chip opens the sources slide-over and highlights the matching
  // card in it; auto-clear the highlight after a beat so it reads as a
  // transient ping rather than a sticky selection.
  const handleSelectSource = useCallback((key: string) => {
    setHighlightedSourceKey(key);
    setIsSourcesDialogOpen(true);
    const t = setTimeout(() => setHighlightedSourceKey((current) => (current === key ? null : current)), 1500);
    return () => clearTimeout(t);
  }, []);

  const knowledgeBases = useMemo(() => data?.body?.data || [], [data]);
  const assistants = assistantsData?.body?.data || [];
  const [kbSearch, setKbSearch] = useState('');
  const [assistantSearch, setAssistantSearch] = useState('');

  // Fetch the selected assistant's full record (which includes knowledgeBaseIds)
  // so the KB picker can scope to just the assistant's allowed KBs. The list
  // endpoint doesn't include the KB associations.
  const { data: selectedAssistantData } = restQueryClient.assistants.get.useQuery(
    ['assistant', selectedAssistantId ?? ''],
    { params: { id: selectedAssistantId ?? '' } },
    { enabled: !!selectedAssistantId }
  );
  const assistantKbIds = useMemo<string[] | null>(() => {
    if (!selectedAssistantId) return null;
    const ids = selectedAssistantData?.status === 200 ? selectedAssistantData.body.knowledgeBaseIds : undefined;
    return Array.isArray(ids) ? ids : null;
  }, [selectedAssistantId, selectedAssistantData]);

  // KBs visible in the picker. With an assistant selected, restrict to the
  // assistant's configured KBs (the server enforces this anyway, but the
  // picker should match what's actually being searched).
  const availableKnowledgeBases = useMemo(() => {
    if (!assistantKbIds) return knowledgeBases;
    const allowed = new Set(assistantKbIds);
    return knowledgeBases.filter((kb) => allowed.has(kb.id));
  }, [knowledgeBases, assistantKbIds]);

  // Default the KB selection: with no assistant, all tenant KBs; with an
  // assistant, all of the assistant's KBs. Re-runs whenever availability
  // changes (assistant selection toggled, KBs loaded, etc.).
  useEffect(() => {
    if (availableKnowledgeBases.length > 0) {
      setCurrentKnowledgeBases(availableKnowledgeBases.map((kb) => kb.id));
    } else {
      setCurrentKnowledgeBases([]);
    }
  }, [availableKnowledgeBases]);

  // The chat scroll container is conditionally rendered (only after a
  // conversation exists), so this effect must re-run when that gate flips —
  // mounting the listener once on first render bound it to a null ref and
  // it never re-attached when the container appeared.
  const hasChatContainer = !!conversationId || chatHistory.length > 0;

  // Detect user-initiated scrolls: if the user is far from the bottom we stop
  // auto-following streaming content; once they scroll back near the bottom
  // we resume. We listen on both the chat container and window because nested
  // flex layouts make it ambiguous which element actually receives the scroll;
  // whichever fires, we measure that source's distance from the bottom.
  useEffect(() => {
    if (!hasChatContainer) return;

    const el = chatContainerRef.current;
    const onContainerScroll = () => {
      if (!el) return;
      const dist = el.scrollHeight - (el.scrollTop + el.clientHeight);
      stickToBottomRef.current = dist < 50;
    };
    const onWindowScroll = () => {
      const dist = document.documentElement.scrollHeight - (window.scrollY + window.innerHeight);
      stickToBottomRef.current = dist < 50;
    };

    el?.addEventListener('scroll', onContainerScroll, { passive: true });
    window.addEventListener('scroll', onWindowScroll, { passive: true });
    return () => {
      el?.removeEventListener('scroll', onContainerScroll);
      window.removeEventListener('scroll', onWindowScroll);
    };
  }, [hasChatContainer]);

  const loadConversation = useCallback(
    async (conversationId: string) => {
      try {
        const { body: conversation } = await restQueryClient.chatConversations.get.query({
          params: { id: conversationId }
        });
        setConversationId((conversation as { id: string }).id);
        setChatTitle((conversation as { title?: string }).title || '');
        const conversationData = conversation as { assistantId?: string };
        if (conversationData.assistantId) {
          setSelectedAssistantId(conversationData.assistantId);
        }
        const rawMessages = (conversation as { messages: LoadedMessage[] }).messages;
        setChatHistory(groupHistory(rawMessages));
        setFoundSources([]);
        setChatError(null);
      } catch (error) {
        console.error('Failed to load conversation:', error);
        setChatError(new Error('Failed to load conversation'));
      }
    },
    [restQueryClient]
  );

  // Handle chat ID parameter from URL route
  useEffect(() => {
    if (chatId && chatId !== conversationId) {
      loadConversation(chatId);
    }
  }, [chatId, conversationId, loadConversation]);

  const handleCheckboxChange = (id: string) => {
    setCurrentKnowledgeBases((prev) => (prev.includes(id) ? prev.filter((kbId) => kbId !== id) : [...prev, id]));
  };

  const scrollToBottom = useCallback(() => {
    // The scroll container has pb-36 (144px) so the floating input bar doesn't
    // cover the last message. Calling scrollIntoView({block:'end'}) on a
    // sentinel inside that padding lands 144px short of true bottom, leaving
    // dist=144; the next scroll event then flips stickToBottomRef to false and
    // auto-follow disengages. scrollTo({top:scrollHeight}) clamps to
    // scrollHeight - clientHeight exactly, so dist lands at 0. Window scroll
    // covers the case where nested flex layouts make the page itself the
    // scrolling element.
    const c = chatContainerRef.current;
    if (c) c.scrollTo({ top: c.scrollHeight, behavior: 'auto' });
    window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'auto' });
  }, []);

  // Bubble heights change after the React commit too — markdown finishing
  // layout, code blocks rendering, images loading, Steps disclosures expanding.
  // A state-change effect misses all of those. ResizeObserver fires on every
  // reflow of the message wrapper, so we re-pin to the bottom whenever the
  // user is still in the sticky zone. This was the missing piece in the prior
  // IntersectionObserver attempt: stickiness here is decided by user scroll
  // events, not by a sentinel's visibility, so growing content can't race-flip
  // us off the bottom.
  useEffect(() => {
    if (!hasChatContainer) return;
    const content = chatContentRef.current;
    if (!content) return;
    const ro = new ResizeObserver(() => {
      if (stickToBottomRef.current) scrollToBottom();
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [hasChatContainer, scrollToBottom]);

  const createNewConversation = async (title?: string, assistantId?: string) => {
    try {
      // Validate that selected assistant is in the available assistants list
      if (assistantId) {
        const assistantExists = assistants.some((a) => a.id === assistantId);
        if (!assistantExists) {
          setChatError(new Error('Selected assistant is not available for your tenant'));
          return null;
        }
      }

      const { body: conversation } = await createConversationMutation.mutateAsync({
        body: { title, assistantId, tenantId: activeTenantId || '' }
      });
      setConversationId(conversation.id);
      setSelectedAssistantId(assistantId);
      setChatHistory([]);
      setFoundSources([]);
      setChatTitle('');
      setChatError(null);

      // Update the URL without triggering React Router navigation, so the component
      // stays mounted and the SSE stream callbacks remain valid
      window.history.replaceState(null, '', `/chat/${conversation.id}`);

      return conversation.id;
    } catch (error) {
      console.error('Failed to create conversation', error);
      // Handle API error responses
      if (error && typeof error === 'object' && 'body' in error) {
        const apiError = error as { body?: { message?: string } };
        setChatError(new Error(apiError.body?.message || 'Failed to create conversation'));
      } else {
        setChatError(new Error('Failed to create conversation'));
      }
      return null;
    }
  };

  const handleAssistantSelect = async (assistantId: string) => {
    setIsAssistantModalOpen(false);

    if (!assistantId) {
      // User selected "None" - create conversation without assistant
      const newConversationId = await createNewConversation('New Chat', undefined);
      if (newConversationId) {
        await loadConversation(newConversationId);
      }
      return;
    }

    const assistant = assistants.find((a) => a.id === assistantId);
    if (!assistant) return;

    // Create conversation with assistant
    const newConversationId = await createNewConversation(assistant.name, assistantId);
    if (!newConversationId) return;

    // Reload conversation to get the first message if it was added
    await loadConversation(newConversationId);
  };

  const sendMessage = async () => {
    if (!messageInput.trim()) return;
    if (isAttachmentUploading) return;

    // Create conversation if none exists
    let currentConversationId = conversationId;
    if (!currentConversationId) {
      currentConversationId = await createNewConversation('New Chat');
      if (!currentConversationId) return;
    }

    // Snapshot the ready attachments so the optimistic bubble matches what
    // gets sent; we'll clear the composer state below.
    const sendingAttachments: ChatAttachmentView[] = attachments
      .filter((a) => a.status === 'ready' && a.remoteId)
      .map((a) => ({
        id: a.remoteId!,
        filename: a.file.name,
        mimeType: a.file.type,
        sizeBytes: a.file.size,
        kind: a.kind
      }));
    const sendingIds = readyAttachmentIds;

    // Add user message to chat history
    const userMessage: ChatMessage = {
      role: 'user',
      content: messageInput,
      attachments: sendingAttachments.length > 0 ? sendingAttachments : undefined
    };
    setChatHistory((prev) => [...prev, userMessage]);
    setCurrentChatContent('');
    setCurrentSteps([]);
    setCurrentSources([]);
    setCurrentStatus(THINKING_STATUS);
    setFoundSources([]);
    setChatting(true);
    setChatError(null);
    if (!chatTitle) {
      setChatTitle('');
    }

    const currentMessage = messageInput;
    setMessageInput('');
    clearAttachments();

    const completionPath = `/chat-conversations/${currentConversationId}/completion`;
    const requestBody = {
      prompt: currentMessage,
      kbIds: currentKnowledgeBases,
      assistantId: selectedAssistantId,
      attachmentIds: sendingIds
    };

    // One user prompt → one assistant bubble. We accumulate text and steps
    // across every model turn in the agent loop, then finalize a single bubble
    // when the stream closes (or errors). The local `finalized` flag guards
    // against the rare case where both onclose and onerror could fire.
    let finalized = false;
    const finalizeBubble = () => {
      if (finalized) return;
      finalized = true;
      setCurrentStatus(null);
      setCurrentSources((sources) => {
        setCurrentSteps((steps) => {
          setCurrentChatContent((content) => {
            if (content.trim().length > 0 || steps.length > 0 || sources.length > 0) {
              setChatHistory((hist) => [
                ...hist,
                {
                  role: 'assistant',
                  content,
                  steps: steps.length > 0 ? steps : undefined,
                  sources: sources.length > 0 ? sources : undefined
                }
              ]);
            }
            return '';
          });
          return [];
        });
        return [];
      });
    };

    const requestBodyJson = JSON.stringify(requestBody);
    const bodyHash = await computeXAmzContentSha256(requestBodyJson);

    fetchEventSource(`${config.api.apiUrl}${completionPath}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'x-amz-content-sha256': bodyHash
      },
      body: requestBodyJson,
      onmessage: (event) => {
        const parsed = JSON.parse(event.data);
        const blocks: AssistantBlock[] | undefined = parsed.blocks;

        if (parsed.event === 'on_chat_model_stream') {
          if (Array.isArray(blocks) && blocks.length > 0) {
            for (const b of blocks) {
              if (b.kind === 'text') {
                setCurrentChatContent((prev) => prev + b.text);
                setCurrentStatus(null);
              } else if (b.kind === 'reasoning') {
                setCurrentSteps((prev) => {
                  const last = prev[prev.length - 1];
                  if (last && last.kind === 'reasoning') {
                    return [...prev.slice(0, -1), { kind: 'reasoning', text: last.text + b.text }];
                  }
                  return [...prev, { kind: 'reasoning', text: b.text }];
                });
              }
              // tool_call blocks are intentionally ignored here — on_tool_start
              // fires for the same invocation with the canonical, post-validation
              // signal. Handling both would double-count tool calls in the UI.
            }
          } else if (typeof parsed.content === 'string') {
            // Legacy plain-string content path
            setCurrentChatContent((prev) => prev + parsed.content);
            setCurrentStatus(null);
          }
          return;
        }

        if (parsed.event === 'on_tool_start') {
          setCurrentSteps((prev) => [...prev, { kind: 'tool_call', toolName: parsed.toolName, input: parsed.input }]);
          setCurrentStatus(getToolStatusLabel(parsed.toolName));
          return;
        }

        if (parsed.event === 'on_tool_end') {
          setCurrentSteps((prev) => [
            ...prev,
            { kind: 'tool_result', toolName: parsed.toolName, output: parsed.output ?? '' }
          ]);
          setCurrentStatus(DRAFTING_STATUS);
          return;
        }

        if (parsed.event === 'sources') {
          const incoming: ChatSource[] = Array.isArray(parsed.sources) ? parsed.sources : [];
          if (incoming.length > 0) {
            setCurrentSources((prev) => dedupChatSources([...prev, ...incoming]));
          }
          return;
        }

        // Turn boundary emitted on on_chat_model_end. Append the same \n\n
        // separator the server uses when persisting, so consecutive
        // narrations don't run together in the streaming view.
        if (parsed.event === 'turn_end') {
          setCurrentChatContent((prev) => (prev.length > 0 ? prev + '\n\n' : prev));
          return;
        }

        // Server-emitted error event: surface to the user so the chat
        // doesn't appear frozen when the model call fails (e.g. Bedrock
        // validation rejecting a document name).
        if (parsed.event === 'error') {
          const message = typeof parsed.message === 'string' ? parsed.message : 'Chat failed';
          setChatError(new Error(message));
          return;
        }

        // on_chat_model_end is intentionally not finalized here — the agent
        // loop fires one per model turn, but a single user prompt can produce
        // multiple turns (text → tool_call → tool_result → text). We coalesce
        // them all into one bubble at stream close.
      },
      onclose: () => {
        finalizeBubble();
        setChatting(false);
      },
      onerror: (err) => {
        console.error('Chat API error:', err);
        console.error('Error details:', {
          message: err.message,
          status: err.status,
          statusText: err.statusText,
          url: err.url
        });
        finalizeBubble();
        setChatError(new Error(`Chat failed: ${err.message}`));
        setChatting(false);
        throw err;
      }
    });
  };

  const onMessageSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (messageInput.length === 0) {
      return;
    }
    scrollToBottom();
    sendMessage();
  };

  const MAX_TEXTAREA_HEIGHT = 200;

  const resizeTextarea = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const clamped = Math.min(textarea.scrollHeight, MAX_TEXTAREA_HEIGHT);
    textarea.style.height = clamped + 'px';
    textarea.style.overflowY = textarea.scrollHeight > MAX_TEXTAREA_HEIGHT ? 'auto' : 'hidden';
  }, []);

  const handleTextareaKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (messageInput.trim().length === 0) return;
      scrollToBottom();
      sendMessage();
    }
  };

  // Focus the textarea and capture input when the user starts typing anywhere on the page
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      if (document.activeElement === textarea) return;

      // Ignore modifier-only keys and shortcut combos (Ctrl/Cmd+key)
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      // Ignore non-printable keys
      if (e.key.length !== 1) return;
      // Don't steal focus from other inputs/textareas/contenteditable
      const active = document.activeElement;
      if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active instanceof HTMLElement && active.isContentEditable)
      ) {
        return;
      }

      e.preventDefault();
      textarea.focus();
      setMessageInput((prev) => prev + e.key);
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Reset textarea height when input is cleared (after sending)
  useEffect(() => {
    resizeTextarea();
  }, [messageInput, resizeTextarea]);

  // Title generation runs async in a lambda after the first model response.
  // Instead of long-polling, fire two short backoff fetches; if the lambda is
  // delayed past that, the user can rename manually via the inline editor.
  useEffect(() => {
    if (!conversationId || chatTitle || chatting) return;

    let cancelled = false;
    const tryFetchTitle = async () => {
      try {
        const { body: conversation } = await restQueryClient.chatConversations.get.query({
          params: { id: conversationId }
        });
        const title = (conversation as { title?: string }).title;
        if (title && title !== 'New Chat') {
          if (!cancelled) setChatTitle(title);
          return true;
        }
      } catch {
        /* swallow — best-effort */
      }
      return false;
    };

    const timers: ReturnType<typeof setTimeout>[] = [];
    const schedule = (delay: number) =>
      timers.push(
        setTimeout(async () => {
          if (cancelled) return;
          await tryFetchTitle();
        }, delay)
      );

    schedule(3000);
    schedule(8000);

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [conversationId, chatTitle, chatting, restQueryClient]);

  return (
    <div className="flex-1 flex flex-col min-h-0 justify-center items-center overflow-hidden">
      {(conversationId || chatHistory.length > 0) && (
        <div className="flex-1 flex flex-col min-h-0 w-full">
          <div
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto w-full pb-36 break-words mx-auto"
            style={{ containIntrinsicSize: 'auto', overflowAnchor: 'auto' }}
          >
            <div ref={chatContentRef}>
              <div className="w-full mx-auto mb-4 pt-2 border-b border-(--color-border-default) flex justify-center sticky top-0 bg-(--color-surface-page)">
                <h1 className="text-xl px-4 w-full max-w-[800px] flex items-center">
                  {chatTitle ? (
                    <EditableTitle
                      value={chatTitle}
                      onSave={handleTitleSave}
                      disabled={!conversationId}
                      ariaLabel="Edit chat title"
                    />
                  ) : (
                    <>
                      New Chat <LoadingSpinner className="ml-2" />
                    </>
                  )}
                  <SourcesBadge sources={aggregatedSources} onClick={() => setIsSourcesDialogOpen(true)} />
                </h1>
              </div>
              {foundSources.length > 0 && (
                <div className="flex w-full mx-auto mb-4 px-4 max-w-[800px] justify-center">
                  <div className="w-full max-w-[800px] grid grid-cols-3 md:grid-cols-4 gap-2 mb-4">
                    {foundSources.map((source) => {
                      return (
                        <div key={source.id} className="min-w-0">
                          <button
                            key={source.id}
                            type="button"
                            tabIndex={0}
                            title={`Source: ${source.name}`}
                            disabled={!source.uri}
                            onClick={() => {
                              if (source.uri) {
                                window.open(source.uri, '_blank');
                              }
                            }}
                            className="w-full h-full text-xs border border-(--color-border-default) cursor-pointer bg-(--color-surface-subtle) rounded-lg p-2 items-start text-left"
                          >
                            <span className="line-clamp-2">{source.name}</span>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {chatHistory.map((chat, index) => {
                const hasSteps = chat.role === 'assistant' && (chat.steps?.length ?? 0) > 0;
                const hasSources = chat.role === 'assistant' && (chat.sources?.length ?? 0) > 0;
                const hasAttachments = chat.role === 'user' && (chat.attachments?.length ?? 0) > 0;
                if (!hasSteps && !hasSources && !hasAttachments && chat.content.trim().length === 0) return null;
                return (
                  <ChatBubble
                    key={index}
                    messageRole={chat.role}
                    content={chat.content}
                    steps={chat.role === 'assistant' ? chat.steps : undefined}
                    sources={chat.role === 'assistant' ? chat.sources : undefined}
                    attachments={chat.role === 'user' ? chat.attachments : undefined}
                    apiUrl={config.api.apiUrl}
                    highlightedSourceKey={highlightedSourceKey}
                    onSelectSource={handleSelectSource}
                    defaultStepsExpanded={showReasoning}
                  />
                );
              })}
              {(currentChatContent.trim().length > 0 || currentSteps.length > 0 || currentSources.length > 0) && (
                <ChatBubble
                  messageRole="assistant"
                  content={currentChatContent}
                  steps={currentSteps.length > 0 ? currentSteps : undefined}
                  sources={currentSources.length > 0 ? currentSources : undefined}
                  highlightedSourceKey={highlightedSourceKey}
                  onSelectSource={handleSelectSource}
                  defaultStepsExpanded={showReasoning}
                />
              )}
              {chatError && (
                <div className="flex mb-4 px-4 w-full mx-auto max-w-[800px]">
                  <AlertBanner type="error" className="w-full" heading="Error">
                    Error: {chatError instanceof Error ? chatError.message : 'Unknown error'}
                  </AlertBanner>
                </div>
              )}

              {chatting && currentStatus && (
                <div className="flex items-center gap-2 px-4 mt-2 mx-auto max-w-[800px] text-sm text-(--color-text-muted)">
                  <LoadingSpinner className="h-4 w-4" />
                  <span aria-live="polite">{currentStatus}</span>
                </div>
              )}
              {chatting && !currentStatus && (
                <div className="w-full text-center mt-4">
                  <LoadingSpinner className="mx-auto" />
                </div>
              )}
              <div ref={bottomSentinelRef} className="w-full mt-5 h-5" />
            </div>
          </div>
        </div>
      )}
      <div
        className={`${
          conversationId || chatHistory.length > 0 ? 'fixed inset-x-0 lg:pl-64' : 'relative w-full'
        } text-center bottom-0`}
      >
        <div className="max-w-[800px] mx-auto p-4 bg-(--color-surface-page) shadow-[0_0_2rem_1rem_var(--color-chat-fade)]">
          {!conversationId &&
            (activeTenantId ? (
              <>
                <img
                  src={`${config.api.apiUrl}/tenants/${activeTenantId}/logo`}
                  width={250}
                  alt="Logo"
                  className="mx-auto mb-4 dark:hidden"
                />
                <img
                  src={`${config.api.apiUrl}/tenants/${activeTenantId}/logo?theme=dark`}
                  width={250}
                  alt="Logo"
                  className="mx-auto mb-4 hidden dark:block"
                />
              </>
            ) : (
              <>
                <img src={logo} width={180} alt="Archie" className="mx-auto mb-4 dark:hidden" />
                <img src={logoDark} width={180} alt="Archie" className="mx-auto mb-4 hidden dark:block" />
              </>
            ))}
          <form onSubmit={onMessageSubmit}>
            <label htmlFor="messageInput" className="sr-only">
              Message
            </label>
            <div
              className={`w-full border ${
                isDragOver ? 'border-(--color-accent) ring-2 ring-(--color-accent)' : 'border-(--color-border-default)'
              } flex flex-col rounded-2xl`}
              onDragEnter={(e) => {
                e.preventDefault();
                if (e.dataTransfer.types.includes('Files')) setIsDragOver(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (e.dataTransfer.types.includes('Files')) setIsDragOver(true);
              }}
              onDragLeave={(e) => {
                if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                setIsDragOver(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
              }}
            >
              {attachments.length > 0 && (
                <>
                  <div role="list" aria-label="Pending attachments" className="flex flex-wrap gap-2 px-3 pt-3">
                    {attachments.map((a) => (
                      <AttachmentChip
                        key={a.localId}
                        attachment={a}
                        onRemove={removeAttachment}
                        onRetry={retryUpload}
                        apiUrl={config.api.apiUrl}
                      />
                    ))}
                  </div>
                  <div aria-live="polite" className="sr-only">
                    {attachments.length === 1
                      ? `1 attachment ready to send`
                      : `${attachments.length} attachments ready to send`}
                  </div>
                </>
              )}
              <textarea
                ref={textareaRef}
                name="messageInput"
                autoComplete="off"
                value={messageInput}
                placeholder="Ask anything!"
                id="messageInput"
                rows={1}
                maxLength={10000}
                className="chat-textarea w-full max-w-full resize-none overflow-y-hidden border-0 outline-none bg-transparent [font:inherit] py-4 px-5 leading-normal focus:!outline-none focus:!shadow-none focus:!border-0 text-(--color-text-primary)"
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  setMessageInput(e.target.value);
                }}
                onKeyDown={handleTextareaKeyDown}
                onPaste={(e) => {
                  // Files arrive on clipboardData.files; if the paste also has
                  // text, we still let the textarea handle that part by not
                  // calling preventDefault unless we're consuming files.
                  const files = e.clipboardData?.files;
                  if (files && files.length > 0) {
                    e.preventDefault();
                    addFiles(files);
                  }
                }}
              />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="sr-only"
                onChange={(e) => {
                  if (e.target.files?.length) addFiles(e.target.files);
                  // Reset so selecting the same file again still fires onChange.
                  e.target.value = '';
                }}
              />
              <div className="flex justify-between items-center py-2 px-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="p-1 text-(--color-text-muted) hover:text-(--color-text-secondary)"
                  aria-label="Attach file"
                  title="Attach file"
                >
                  <PaperClipIcon className="h-5 w-5" />
                </button>
                <div className="flex items-center">
                  {!messageInput.length && (
                    <span className="flex items-center p-1 text-zinc-400">
                      <ChatBubbleLeftIcon className="h-5 w-5" />
                    </span>
                  )}
                  {messageInput.length > 0 && (
                    <button
                      type="submit"
                      disabled={isAttachmentUploading}
                      className="p-1 text-(--color-accent) hover:text-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      aria-label="Send message"
                    >
                      <PaperAirplaneIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
            {attachmentGlobalError && (
              <div className="mt-2">
                <AlertBanner type="error" className="w-full" heading="Attachment error">
                  {attachmentGlobalError}
                </AlertBanner>
              </div>
            )}
          </form>
          <div className="mt-4 flex justify-between items-center gap-2">
            <div>
              <ChatCountBubble />
            </div>
            <div className="flex items-center gap-2">
              {/* Assistant Selector */}
              {assistants.length > 0 && (
                <div className="flex items-center">
                  {conversationId ? (
                    <span id="assistant-display" className="text-(--color-text-muted)">
                      {selectedAssistantId
                        ? 'Assistant: ' + (assistants.find((a) => a.id === selectedAssistantId)?.name || 'Unknown')
                        : 'No assistant selected'}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsAssistantModalOpen(true)}
                      className="p-1 text-(--color-text-muted) hover:text-(--color-text-secondary) mr-1"
                      title="Select assistant"
                    >
                      <UserGroupIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>
              )}

              {/* Show Reasoning Toggle */}
              <button
                type="button"
                onClick={() => setShowReasoning(!showReasoning)}
                aria-pressed={showReasoning}
                className={`p-1 ${showReasoning ? 'text-(--color-accent)' : 'text-(--color-text-muted) hover:text-(--color-text-secondary)'}`}
                title={showReasoning ? 'Hide reasoning by default' : 'Show reasoning by default'}
              >
                <LightBulbIcon className="h-5 w-5" />
              </button>

              {/* Knowledge Base Settings */}
              <button
                type="button"
                onClick={() => setIsKbModalOpen(true)}
                className="p-1 text-(--color-text-muted) hover:text-(--color-text-secondary)"
                title="Select knowledge bases"
              >
                <Cog6ToothIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Knowledge Base Dialog */}
      <Dialog open={isKbModalOpen} onClose={() => setIsKbModalOpen(false)}>
        {isLoading ? (
          <DialogBody>
            <p>Loading...</p>
          </DialogBody>
        ) : isError ? (
          <DialogBody>
            <p>Error loading knowledge bases.</p>
          </DialogBody>
        ) : knowledgeBases.length === 0 ? (
          <DialogBody>
            <p>No knowledge bases found.</p>
          </DialogBody>
        ) : (
          <>
            <DialogTitle>Select Knowledge Bases</DialogTitle>
            <DialogBody>
              <Input
                type="text"
                name="kb-search"
                placeholder="Search knowledge bases..."
                value={kbSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKbSearch(e.target.value)}
                className="mb-2"
              />
              <ul className="p-0 list-none overflow-y-auto max-h-72">
                {availableKnowledgeBases
                  .filter((kb) => kb.name.toLowerCase().includes(kbSearch.toLowerCase()))
                  .map((kb) => (
                    <li key={kb.id} className="py-1 px-1">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={currentKnowledgeBases.includes(kb.id)}
                          onChange={() => handleCheckboxChange(kb.id)}
                          className="mr-2"
                          aria-label={`Select knowledge base: ${kb.name}`}
                        />
                        <div>
                          <span className="font-bold">{kb.name}:</span> {kb.sourceCount} sources
                        </div>
                      </label>
                    </li>
                  ))}
              </ul>
              {selectedAssistantId && availableKnowledgeBases.length === 0 && (
                <p className="mt-2 text-sm text-(--color-text-muted)">
                  This assistant has no knowledge bases configured.
                </p>
              )}
            </DialogBody>
          </>
        )}
        <DialogActions>
          <Button type="button" plain onClick={() => setIsKbModalOpen(false)}>
            Cancel
          </Button>
          <Button type="button" color="blue" onClick={() => setIsKbModalOpen(false)}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assistant Dialog */}
      <Dialog open={isAssistantModalOpen} onClose={() => setIsAssistantModalOpen(false)}>
        {isAssistantsLoading ? (
          <DialogBody>
            <p>Loading...</p>
          </DialogBody>
        ) : isAssistantsError ? (
          <DialogBody>
            <p>Error loading assistants.</p>
          </DialogBody>
        ) : assistants.length === 0 ? (
          <DialogBody>
            <p>No assistants found.</p>
          </DialogBody>
        ) : (
          <>
            <DialogTitle>Select an Assistant</DialogTitle>
            <DialogBody>
              <Input
                type="text"
                name="assistant-search"
                placeholder="Search assistants..."
                value={assistantSearch}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAssistantSearch(e.target.value)}
                className="mb-2"
              />
              <ul className="p-0 list-none overflow-y-auto max-h-72">
                {assistants
                  .filter((a) => a.name.toLowerCase().includes(assistantSearch.toLowerCase()))
                  .map((assistant) => (
                    <li key={assistant.id} className="py-1 px-1">
                      <button
                        type="button"
                        className="w-full text-left p-4 border border-(--color-border-default) hover:bg-(--color-surface-hover) cursor-pointer rounded-lg"
                        onClick={() => handleAssistantSelect(assistant.id)}
                      >
                        <div className="font-bold">{assistant.name}</div>
                        {assistant.description && (
                          <div className="text-sm text-(--color-text-secondary)">{assistant.description}</div>
                        )}
                      </button>
                    </li>
                  ))}
              </ul>
            </DialogBody>
          </>
        )}
        <DialogActions>
          <Button type="button" plain onClick={() => setIsAssistantModalOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <SourcesDialog
        open={isSourcesDialogOpen}
        onClose={() => setIsSourcesDialogOpen(false)}
        sources={aggregatedSources}
        highlightedKey={highlightedSourceKey}
      />
    </div>
  );
};

export default Chat;
